/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GallerySubmission } from 'src/openapi/models';
import {
  fetchCompetitionSubmissions,
  toggleSubmissionLike,
} from '../actions/gallery';
import { GalleryState, VotingPhase } from '../types/gallery';

const initialState: GalleryState = {
  submissions: [],
  phase: 'open',
  votingEnabled: true,
  resultOn: null,
  total: 0,
  page: 1,
  totalPages: 1,
  isFetching: false,
};

const gallerySlice = createSlice({
  name: 'gallery',
  initialState,
  reducers: {
    resetGallery: () => initialState,
    // Seed the store with a single post (single-post page) so the shared
    // SubmissionCard's like toggle flows through toggleSubmissionLike.fulfilled.
    setGallerySubmissions: (
      state,
      action: PayloadAction<{
        submissions: GallerySubmission[];
        phase?: VotingPhase;
        votingEnabled?: boolean;
      }>,
    ) => {
      state.submissions = action.payload.submissions;
      state.phase = action.payload.phase ?? state.phase;
      state.votingEnabled = action.payload.votingEnabled ?? state.votingEnabled;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompetitionSubmissions.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchCompetitionSubmissions.fulfilled, (state, action) => {
        const p = action.payload;
        const page = p.page ?? 1;
        state.isFetching = false;
        // Page 1 replaces; subsequent pages append (load-more).
        state.submissions =
          page > 1
            ? [...state.submissions, ...(p.submissions ?? [])]
            : (p.submissions ?? []);
        state.phase = (p.phase as VotingPhase) ?? 'open';
        state.votingEnabled = p.voting_enabled ?? true;
        state.resultOn = p.result_on ?? null;
        state.total = p.total ?? 0;
        state.page = page;
        state.totalPages = p.total_pages ?? 1;
      })
      .addCase(fetchCompetitionSubmissions.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(toggleSubmissionLike.fulfilled, (state, action) => {
        const { registrationId, liked, like_count: likeCount } = action.payload;
        const item = state.submissions.find((s) => s.id === registrationId);
        if (item) {
          item.is_liked = liked;
          item.like_count = likeCount;
        }
      });
  },
});

export const { resetGallery, setGallerySubmissions } = gallerySlice.actions;
export default gallerySlice.reducer;
